import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  TrendingUp, 
  Building, 
  DollarSign, 
  Percent,
  Download,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  Home,
  Award,
  TrendingDown,
  Users
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Line, Bar, Pie } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

const PerformanceReportPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [generateSummary, setGenerateSummary] = useState(false);
  const [summaryModel, setSummaryModel] = useState('qwen'); // 'qwen' or 'gemini'
  const [executiveSummary, setExecutiveSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Fetch portfolio data
  const fetchPortfolioData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get user_id and selected owner from Dashboard state or URL
      const userId = user?.id;
      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Try to get selectedOwner from URL params or localStorage (set by Dashboard)
      const urlParams = new URLSearchParams(window.location.search);
      const ownerEntity = urlParams.get('owner') || localStorage.getItem('selectedOwner') || 'all';

      const response = await fetch(`/api/reports/portfolio/performance?user_id=${userId}&owner_entity=${ownerEntity}`);
      const result = await response.json();

      if (!result.ok) {
        throw new Error(result.error || 'Failed to fetch portfolio data');
      }

      setData(result.data);
    } catch (err) {
      console.error('[PORTFOLIO] Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchPortfolioData();
    }
  }, [user]);

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Format currency with 2 decimal places (for tenant list)
  const formatCurrencyDetailed = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format percentage
  const formatPercent = (value) => {
    if (value == null || isNaN(value)) return '0.00%';
    return `${value.toFixed(2)}%`;
  };

  // Generate Executive Summary with AI
  const generateExecutiveSummary = async (portfolioData) => {
    try {
      setSummaryLoading(true);
      
      // Prepare data for AI
      const summaryData = {
        totalValue: portfolioData.totalValue,
        totalRent: portfolioData.totalRent,
        occupancyRate: portfolioData.occupancyRate,
        avgYield: portfolioData.avgYield,
        propertyCount: portfolioData.properties?.length || 0,
        tenantCount: portfolioData.tenants?.length || 0
      };
      
      const prompt = `Du bist ein professioneller Immobilien-Analyst. Erstelle eine prägnante Executive Summary (max. 150 Wörter) für diesen Performance Report:

Portfolio-Daten:
- Gesamtwert: ${formatCurrency(summaryData.totalValue)}
- Jahreskaltmiete: ${formatCurrency(summaryData.totalRent)}
- Auslastung: ${formatPercent(summaryData.occupancyRate)}
- Durchschnittliche Rendite: ${formatPercent(summaryData.avgYield)}
- Anzahl Immobilien: ${summaryData.propertyCount}
- Anzahl Mieter: ${summaryData.tenantCount}

Die Zusammenfassung soll:
1. Die wichtigsten Kennzahlen hervorheben
2. Die Performance bewerten
3. Kurze Handlungsempfehlungen geben

Schreibe professionell und prägnant auf Deutsch.`;
      
      let summary = '';
      
      if (summaryModel === 'qwen') {
        // Call AI Summary Service (local)
        const response = await fetch('/api/ai-summary/generate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt, model: 'qwen2.5:7b' })
        });
        
        const result = await response.json();
        if (result.ok) {
          summary = result.summary;
        } else {
          throw new Error(result.error || 'Failed to generate summary');
        }
      } else if (summaryModel === 'gemini') {
        // Call AI Summary Service with Gemini
        const response = await fetch('/api/ai-summary/generate-gemini', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt })
        });
        
        const result = await response.json();
        if (result.ok) {
          summary = result.summary;
        } else {
          throw new Error(result.error || 'Failed to generate summary');
        }
      }
      
      setExecutiveSummary(summary);
      return summary;
    } catch (err) {
      console.error('[AI] Error generating summary:', err);
      setExecutiveSummary('Fehler beim Generieren der Zusammenfassung.');
      return null;
    } finally {
      setSummaryLoading(false);
    }
  };

  // Multi-Page PDF Export with Tenant List
  const exportToPDF = async () => {
    try {
      // Generate Executive Summary if enabled
      if (generateSummary && data) {
        const summary = await generateExecutiveSummary(data);
        setExecutiveSummary(summary);
        
        // Wait for summary to render
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Get the report element
      const reportElement = document.getElementById('performance-report');
      if (!reportElement) {
        throw new Error('Report element not found');
      }
      
      // Clone the element for PDF generation
      const clone = reportElement.cloneNode(true);
      clone.classList.add('print-mode');
      clone.classList.remove('dark');
      
      // Hide buttons and interactive elements
      const buttons = clone.querySelectorAll('button');
      buttons.forEach(btn => btn.style.display = 'none');
      
      // Minimal print CSS - don't send all stylesheets (too large)
      const css = `
        @page { size: A4 landscape; margin: 10mm; }
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; margin: 0; padding: 20px; }
        * { box-sizing: border-box; }
        button, [class*="Button"] { display: none !important; }
        table { width: 100%; border-collapse: collapse; margin: 10px 0; }
        th, td { padding: 8px; text-align: left; border: 1px solid #ddd; }
        thead { background: #f3f4f6; }
        thead th { font-weight: 600; border-bottom: 2px solid #0B376D; }
      `;
      
      // Get HTML content
      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Performance Report</title>
        </head>
        <body>
          ${clone.outerHTML}
        </body>
        </html>
      `;
      
      console.log('[PDF Export] HTML length:', html.length);
      console.log('[PDF Export] CSS length:', css.length);
      
      // Call WeasyPrint backend
      const response = await fetch('/api/pdf/weasyprint/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          html: html,
          css: css,
          filename: `Performance_Report_${new Date().toISOString().split('T')[0]}.pdf`
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate PDF');
      }
      
      // Download PDF
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Performance_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      alert('PDF wurde erfolgreich generiert!');
      
      // OLD CODE - html2canvas + jsPDF (commented out)
      /*
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;

      // === OLD CODE - html2canvas approach ===
      // Kept for reference, can be removed later
      /*
      const reportElement = document.getElementById('performance-report');
      
      // Create hidden clone for PDF generation (don't modify visible page)
      const clone = reportElement.cloneNode(true);
      clone.style.position = 'absolute';
      clone.style.left = '-9999px';
      clone.style.top = '0';
      
      // Fixed width for consistent PDF rendering (independent of viewport)
      clone.style.width = '1920px';
      clone.style.minHeight = 'auto';
      clone.style.height = 'auto';
      clone.style.overflow = 'visible';
      
      clone.classList.add('print-mode');
      clone.classList.remove('dark');
      
      // Remove controls from clone
      const controlsInClone = clone.querySelectorAll('.print\\:hidden, [class*="Executive Summary"]');
      controlsInClone.forEach(el => {
        if (el.textContent.includes('Executive Summary generieren') || 
            el.textContent.includes('Modell:') ||
            el.textContent.includes('KI-generierte Zusammenfassung')) {
          el.remove();
        }
      });
      
      // Insert Executive Summary if generated
      if (aiSummary) {
        const summaryDiv = document.createElement('div');
        summaryDiv.className = 'mb-6 p-6 bg-blue-50 border-l-4 border-blue-600 rounded-r-lg';
        summaryDiv.innerHTML = `
          <h2 class="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <span class="text-blue-600">✨</span>
            Executive Summary
          </h2>
          <div class="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">${aiSummary}</div>
          <p class="text-xs text-gray-500 mt-3 italic">Generiert mit ${summaryModel === 'qwen' ? 'Qwen 2.5' : 'Gemini'}</p>
        `;
        
        // Insert after header, before first card
        const firstCard = clone.querySelector('.grid');
        if (firstCard && firstCard.parentNode) {
          firstCard.parentNode.insertBefore(summaryDiv, firstCard);
        }
      }
      
      // Temporarily append clone to body
      document.body.appendChild(clone);
      
      // Wait for layout to settle and charts to render
      await new Promise(resolve => setTimeout(resolve, 500));

      const canvas1 = await html2canvas(clone, {
        scale: 2,
        logging: false,
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: 1920,
        windowHeight: clone.scrollHeight
      });

      // Remove clone immediately after capture
      document.body.removeChild(clone);

      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const padding = 10;
      const pageWidth = 297;
      const pageHeight = 210;
      const contentWidth = pageWidth - (padding * 2);
      const contentHeight = pageHeight - (padding * 2);

      // Add Page 1: Performance Report
      const imgData1 = canvas1.toDataURL('image/png');
      const img1Width = contentWidth;
      const img1Height = (canvas1.height * img1Width) / canvas1.width;
      
      // If content is taller than one page, split into multiple pages
      if (img1Height > contentHeight) {
        let remainingHeight = img1Height;
        let sourceY = 0;
        let pageNum = 0;
        
        while (remainingHeight > 0) {
          if (pageNum > 0) {
            pdf.addPage();
          }
          
          const heightForThisPage = Math.min(contentHeight, remainingHeight);
          const sourceHeight = (heightForThisPage / img1Height) * canvas1.height;
          
          // Create a temporary canvas for this slice
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = canvas1.width;
          tempCanvas.height = sourceHeight;
          const tempCtx = tempCanvas.getContext('2d');
          tempCtx.drawImage(canvas1, 0, sourceY, canvas1.width, sourceHeight, 0, 0, canvas1.width, sourceHeight);
          
          const sliceData = tempCanvas.toDataURL('image/png');
          pdf.addImage(sliceData, 'PNG', padding, padding, img1Width, heightForThisPage);
          
          sourceY += sourceHeight;
          remainingHeight -= heightForThisPage;
          pageNum++;
        }
      } else {
        // Content fits on one page
        pdf.addImage(imgData1, 'PNG', padding, padding, img1Width, img1Height);
      }

      // Tenant list is now rendered from HTML/DOM (no separate jsPDF table needed)

      // Save PDF
      pdf.save(`Performance_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      */
      
    } catch (err) {
      console.error('[PDF Export] Error:', err);
      alert('PDF-Export fehlgeschlagen: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Lade Portfolio-Daten...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <p className="text-center text-gray-600 dark:text-gray-400">{error}</p>
            <Button onClick={fetchPortfolioData} className="w-full mt-4">
              <RefreshCw className="w-4 h-4 mr-2" />
              Erneut versuchen
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If no data yet, show loading
  if (!data) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Lade Portfolio-Daten...</p>
        </div>
      </div>
    );
  }

  const {
    totalProperties,
    totalValue,
    totalRentYear,
    totalCashflow,
    avgYield,
    avgOccupancy,
    avgDSCR,
    avgLTV,
    properties,
    totalNOI,
    portfolioCapRate,
    totalIncomeValue,
    portfolioValueDeviation,
  } = data;

  // Top 3 and Bottom 3 performers (only show if 2+ properties)
  const sortedByYield = properties ? [...properties].sort((a, b) => (b.grossYield || 0) - (a.grossYield || 0)) : [];
  const topPerformers = sortedByYield.slice(0, 3);
  const bottomPerformers = sortedByYield.slice(-3).reverse();
  const showPerformers = properties && properties.length >= 2;

  // Chart data for property yields
  const yieldChartData = {
    labels: properties?.map(p => p.name) || [],
    datasets: [{
      label: 'Rendite (%)',
      data: properties?.map(p => p.grossYield) || [],
      backgroundColor: 'rgba(34, 197, 94, 0.5)',
      borderColor: 'rgb(34, 197, 94)',
      borderWidth: 2
    }]
  };

  // Chart data for cashflow
  const cashflowChartData = {
    labels: properties?.map(p => p.name) || [],
    datasets: [{
      label: 'Cashflow (€/Jahr)',
      data: properties?.map(p => p.cashflow) || [],
      backgroundColor: properties?.map(p => 
        p.cashflow >= 0 ? 'rgba(34, 197, 94, 0.5)' : 'rgba(239, 68, 68, 0.5)'
      ) || [],
      borderColor: properties?.map(p => 
        p.cashflow >= 0 ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)'
      ) || [],
      borderWidth: 2
    }]
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        .print-mode { background: #ffffff !important; color: #1a1a1a !important; }
        .print-mode * { box-shadow: none !important; border-radius: 4px !important; }
        .print-mode h1, .print-mode h2, .print-mode h3 { color: #0B376D !important; }
        .print-mode .print-header-info { display: block !important; }
        .print-mode .print\:hidden { display: none !important; }
        @media print {
          .print\:hidden { display: none !important; visibility: hidden !important; }
        }
        .print-mode button, .print-mode [class*="Button"] { display: none !important; }
        .print-mode .bg-blue-50, .print-mode .bg-green-50, .print-mode .bg-purple-50, .print-mode .bg-emerald-50,
        .print-mode .dark\\:bg-gray-800, .print-mode .dark\\:bg-blue-900\\/20, .print-mode .dark\\:bg-green-900\\/20,
        .print-mode .dark\\:bg-purple-900\\/20, .print-mode .dark\\:bg-gray-900 { background: #ffffff !important; border: 1.5px solid #e5e7eb !important; }
        .print-mode .text-blue-600, .print-mode .dark\\:text-blue-400, .print-mode .dark\\:text-blue-100 { color: #0B376D !important; }
        .print-mode .text-green-600, .print-mode .dark\\:text-green-400, .print-mode .dark\\:text-green-100 { color: #16a34a !important; }
        .print-mode .text-purple-600, .print-mode .dark\\:text-purple-400, .print-mode .dark\\:text-purple-100 { color: #9333ea !important; }
        .print-mode .text-emerald-600, .print-mode .dark\\:text-emerald-400, .print-mode .dark\\:text-emerald-100 { color: #059669 !important; }
        .print-mode .bg-gradient-to-br, .print-mode .bg-gradient-to-r { background: #f8f9fa !important; border: 2px solid #0B376D !important; }
        .print-mode .bg-indigo-900\\/10, .print-mode .bg-indigo-800\\/10 { background: #ffffff !important; border: 1px solid #e5e7eb !important; }
        .print-mode .text-indigo-100, .print-mode .text-indigo-200, .print-mode .text-indigo-300,
        .print-mode .dark\\:text-indigo-300, .print-mode .dark\\:text-indigo-100 { color: #0B376D !important; }
        .print-mode .text-orange-400 { color: #f97316 !important; }
        .print-mode .bg-indigo-50, .print-mode .dark\\:bg-indigo-900\\/10 { background: #f0f4f8 !important; border: 1px solid #d1d5db !important; }
        .print-mode .text-indigo-700, .print-mode .dark\\:text-indigo-300 { color: #1e40af !important; }
        .print-mode .border-indigo-100, .print-mode .dark\\:border-indigo-800 { border-color: #cbd5e1 !important; }
        .print-mode .bg-gray-800, .print-mode .dark\\:bg-gray-800 { background: #ffffff !important; border: 1px solid #e5e7eb !important; }
         .print-mode .text-gray-100, .print-mode .text-gray-200, .print-mode .text-gray-300,
        .print-mode .dark\:text-gray-100, .print-mode .dark\:text-gray-200, .print-mode .dark\:text-gray-300 { color: #1f2937 !important; } }
        .print-mode table { background: #ffffff !important; border: 1px solid #d1d5db !important; }
        .print-mode thead { background: #f3f4f6 !important; }
        .print-mode thead th { color: #1f2937 !important; font-weight: 600 !important; border-bottom: 2px solid #0B376D !important; }
        .print-mode tbody tr { border-bottom: 1px solid #e5e7eb !important; }
        .print-mode tbody tr:nth-child(even) { background: #f9fafb !important; }
        .print-mode .text-gray-900, .print-mode .dark\\:text-white { color: #111827 !important; }
         .print-mode .text-gray-600, .print-mode .dark\:text-gray-400 { color: #1f2937 !important; }
        .print-mode .text-gray-700, .print-mode .dark\:text-gray-300 { color: #1f2937 !important; }}
        .print-mode .bg-gray-700, .print-mode .dark\\:bg-gray-700 { background: #e5e7eb !important; color: #1f2937 !important; }
        .print-mode .border-gray-200, .print-mode .dark\\:border-gray-700 { border-color: #d1d5db !important; }
        .print-mode .border-gray-100, .print-mode .dark\\:border-gray-800 { border-color: #e5e7eb !important; }
        .print-mode .text-red-600 { color: #dc2626 !important; }
        .print-mode .text-yellow-600 { color: #ca8a04 !important; }
        .print-mode .from-blue-50, .print-mode .from-green-50, .print-mode .from-purple-50, .print-mode .from-emerald-50,
        .print-mode .dark\\:from-blue-900, .print-mode .dark\\:from-green-900, .print-mode .dark\\:from-purple-900,
        .print-mode .dark\\:from-emerald-900 { background: #ffffff !important; }
        .print-mode .to-blue-100, .print-mode .to-green-100, .print-mode .to-purple-100, .print-mode .to-emerald-100,
        .print-mode .dark\\:to-blue-800, .print-mode .dark\\:to-green-800, .print-mode .dark\\:to-purple-800,
        .print-mode .dark\\:to-emerald-800 { background: #ffffff !important; }
        .print-mode canvas { background: #ffffff !important; }
        .print-mode .bg-orange-50, .print-mode .dark\\:bg-orange-900\\/20, .print-mode .dark\\:bg-orange-900\\/10 { background: #fff7ed !important; border: 1px solid #fed7aa !important; }
        .print-mode .border-orange-200, .print-mode .dark\\:border-orange-800 { border-color: #fed7aa !important; }
        .print-mode .text-orange-700, .print-mode .dark\\:text-orange-300 { color: #c2410c !important; }
        .print-mode .bg-green-50, .print-mode .dark\\:bg-green-900\\/10 { background: #f0fdf4 !important; border: 1px solid #bbf7d0 !important; }
        .print-mode .border-green-200, .print-mode .dark\\:border-green-800 { border-color: #bbf7d0 !important; }
        .print-mode .text-green-700, .print-mode .dark\\:text-green-300 { color: #15803d !important; }
          /* All dark backgrounds → light gray */
        .print-mode .bg-gray-900, .print-mode .dark\:bg-gray-900,
        .print-mode .bg-gray-800, .print-mode .dark\:bg-gray-800,
        .print-mode .bg-slate-800, .print-mode .dark\:bg-slate-800,
        .print-mode .bg-slate-900, .print-mode .dark\:bg-slate-900,
        .print-mode .bg-blue-900, .print-mode .dark\:bg-blue-900,
        .print-mode .bg-indigo-900, .print-mode .dark\:bg-indigo-900 {
          background: #f8f9fa !important;
          border: 1px solid #e5e7eb !important;
          color: #1f2937 !important;
        }
        /* Catch-all for any dark:bg- classes */
        .print-mode [class*="dark:bg-"] {
          background: #f8f9fa !important;
          color: #1f2937 !important;
        } }
        .print-mode .border-blue-900, .print-mode .dark\\:border-blue-900 { border-color: #e5e7eb !important; }
        .print-mode .border-indigo-900, .print-mode .dark\\:border-indigo-900 { border-color: #cbd5e1 !important; }
        .print-mode h3, .print-mode .text-gray-100, .print-mode .dark\\:text-gray-100 { color: #111827 !important; }
        .print-mode .text-blue-400, .print-mode .dark\\:text-blue-400 { color: #0B376D !important; }
        
        /* Hide charts in PDF */
        .print-mode .chart-section { display: none !important; }
        
        /* Spacing between sections */
        .print-mode .kpi-cards { margin-bottom: 3rem !important; }
        .print-mode .ertragswert-section { 
          margin-bottom: 3rem !important;
          page-break-inside: avoid !important;
        }
        
        /* Page break rules */
        .print-mode .performer-section { 
          page-break-before: always !important;
          page-break-inside: avoid !important;
          margin-top: 2rem !important;
          margin-bottom: 3rem !important;
        }
        .print-mode .property-details-section { 
          page-break-before: always !important; 
          page-break-inside: avoid !important;
          padding-top: 1rem !important;
        }
        .print-mode .property-details-section .card-header,
        .print-mode .property-details-section h2 { 
          page-break-after: avoid !important; 
        }
        .print-mode .tenant-list-section { page-break-before: always; }
        .print-mode .tenant-property-block { page-break-inside: avoid; }
        .print-mode .tenant-property-block + .tenant-property-block { page-break-before: auto; }
        
        /* Badge centering fixes */
        .print-mode [class*="rounded-full"] {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          line-height: 1 !important;
          vertical-align: middle !important;
        }
        .print-mode .bg-green-100,
        .print-mode .bg-blue-100,
        .print-mode .bg-green-200,
        .print-mode .bg-blue-200 {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          line-height: 1 !important;
          padding: 4px 10px !important;
          vertical-align: middle !important;
        }
        
        /* Table zebra stripes - make even rows light gray not black */
        .print-mode tbody tr:nth-child(even) {
          background: #f9fafb !important;
          color: #1f2937 !important;
        }
        .print-mode tbody tr:nth-child(even) td {
          background: #f9fafb !important;
          color: #1f2937 !important;
        }
        .print-mode tbody tr:nth-child(odd) {
          background: #ffffff !important;
          color: #1f2937 !important;
        }
        .print-mode tbody tr:nth-child(odd) td {
          background: #ffffff !important;
          color: #1f2937 !important;
        }
        
        /* Portfolio summary box fixes */
        .print-mode .portfolio-summary-box { overflow: visible !important; page-break-before: avoid !important; }
        .print-mode .portfolio-summary-box .text-xl { line-height: 1.75 !important; padding-bottom: 0.25rem !important; }
        
        /* Page break improvements */
        .print-mode .ertragswert-section { page-break-inside: avoid !important; page-break-after: always !important; }
        .print-mode .performer-section { page-break-before: always !important; page-break-inside: avoid !important; margin-top: 0 !important; }
        .print-mode .property-details-section { page-break-before: always !important; }
        .print-mode .mieterliste-section { page-break-inside: avoid !important; }
      ` }} />
      <div className="min-h-screen bg-gray-50 dark:bg-black p-6">
      <div id="performance-report" className="max-w-7xl mx-auto space-y-6">
        {/* Print-Only Header with Owner & Date */}
        <div className="print-header-info hidden print:block mb-6">
          <div className="flex justify-between items-start border-b-2 border-blue-600 pb-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Performance Report</h1>
              <p className="text-gray-600 mt-1">Portfolio von {user?.email?.split('@')[0] || 'Eigentümer'}</p>
            </div>
            <div className="text-right text-sm text-gray-600">
              <p>Erstellt am: {new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</p>
              <p className="text-xs text-gray-500 mt-1">Clara360 Immobilienverwaltung</p>
            </div>
          </div>
        </div>
        
        {/* Executive Summary (Print Only) */}
        {executiveSummary && (
          <div className="print-header-info hidden print:block mb-6 p-6 bg-blue-50 border-l-4 border-blue-600 rounded-r-lg">
            <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="text-blue-600">✨</span>
              Executive Summary
            </h2>
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
              {executiveSummary}
            </p>
            <p className="text-xs text-gray-500 mt-3 italic">
              Generiert mit {summaryModel === 'qwen' ? 'Qwen 2.5' : 'Gemini'}
            </p>
          </div>
        )}

        {/* Header */}
        <div className="print:hidden mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              📊 Performance Report
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Gesamtübersicht Ihres Immobilien-Portfolios
            </p>
          </div>
          <div className="flex gap-3">
            <Button onClick={fetchPortfolioData} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Aktualisieren
            </Button>
            <Button onClick={exportToPDF} className="bg-blue-600 hover:bg-blue-700">
              <Download className="w-4 h-4 mr-2" />
              PDF Export
            </Button>
          </div>
        </div>
        
        {/* Executive Summary Controls */}
        <div className="print:hidden mt-4 mb-8 p-4 bg-gray-50 dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={generateSummary}
                onChange={(e) => setGenerateSummary(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Executive Summary generieren
              </span>
            </label>
            
            {generateSummary && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Modell:</span>
                <select
                  value={summaryModel}
                  onChange={(e) => setSummaryModel(e.target.value)}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="qwen">Qwen 2.5 (lokal, schnell)</option>
                  <option value="gemini">Gemini (Cloud)</option>
                </select>
                {summaryLoading && (
                  <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                    Generiere Zusammenfassung...
                  </span>
                )}
              </div>
            )}
          </div>
          {generateSummary && (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              💡 Die KI-generierte Zusammenfassung erscheint auf der ersten Seite des PDFs. Export dauert ca. 5-10 Sekunden länger.
            </p>
          )}
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Value */}
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 border-blue-200 dark:border-blue-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-blue-700 dark:text-blue-300 flex items-center">
                <Building className="w-4 h-4 mr-2" />
                Portfolio-Wert
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-900 dark:text-blue-100">
                {formatCurrency(totalValue)}
              </div>
              <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">
                {totalProperties} Immobilien
              </p>
            </CardContent>
          </Card>

          {/* Average Yield */}
          <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900 dark:to-green-800 border-green-200 dark:border-green-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-green-700 dark:text-green-300 flex items-center">
                <TrendingUp className="w-4 h-4 mr-2" />
                Ø Rendite
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-900 dark:text-green-100">
                {formatPercent(avgYield)}
              </div>
              <p className="text-sm text-green-600 dark:text-green-400 mt-2">
                Brutto-Mietrendite
              </p>
            </CardContent>
          </Card>

          {/* Occupancy */}
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900 dark:to-purple-800 border-purple-200 dark:border-purple-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-purple-700 dark:text-purple-300 flex items-center">
                <Home className="w-4 h-4 mr-2" />
                Auslastung
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-900 dark:text-purple-100">
                {formatPercent(avgOccupancy)}
              </div>
              <p className="text-sm text-purple-600 dark:text-purple-400 mt-2">
                Vermietungsquote
              </p>
            </CardContent>
          </Card>

          {/* Cashflow */}
          <Card className={`bg-gradient-to-br ${
            totalCashflow >= 0 
              ? 'from-emerald-50 to-emerald-100 dark:from-emerald-900 dark:to-emerald-800 border-emerald-200 dark:border-emerald-700'
              : 'from-red-50 to-red-100 dark:from-red-900 dark:to-red-800 border-red-200 dark:border-red-700'
          }`}>
            <CardHeader className="pb-3">
              <CardTitle className={`text-sm font-medium flex items-center ${
                totalCashflow >= 0 
                  ? 'text-emerald-700 dark:text-emerald-300'
                  : 'text-red-700 dark:text-red-300'
              }`}>
                <DollarSign className="w-4 h-4 mr-2" />
                Cashflow
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${
                totalCashflow >= 0 
                  ? 'text-emerald-900 dark:text-emerald-100'
                  : 'text-red-900 dark:text-red-100'
              }`}>
                {formatCurrency(totalCashflow)}
              </div>
              <p className={`text-sm mt-2 ${
                totalCashflow >= 0 
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-red-600 dark:text-red-400'
              }`}>
                pro Jahr (nach Finanzierung)
              </p>
            </CardContent>
          </Card>
        </div>


        {/* Clara KI Ertragswertverfahren - INSERT AFTER KPI CARDS, BEFORE CHARTS (line 348) */}
        <Card className="ertragswert-section mb-8 bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/30 dark:to-indigo-800/30 border-indigo-200 dark:border-indigo-700 print:bg-white print:border-gray-300">
          <CardHeader>
            <CardTitle className="flex items-center text-indigo-700 dark:text-indigo-300 print:text-indigo-900">
              <DollarSign className="w-5 h-5 mr-2" />
              💰 Clara KI Ertragswertverfahren
            </CardTitle>
            <p className="text-sm text-indigo-600 dark:text-indigo-400 mt-1 print:text-gray-600">
              Bewertung nach Einkommensansatz (Income Approach)
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* NOI (Net Operating Income) */}
              <div className="p-4 bg-white dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800 print:bg-gray-50 print:border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400 uppercase tracking-wide print:text-indigo-900">
                    NOI
                  </span>
                  <TrendingUp className="w-4 h-4 text-indigo-500" />
                </div>
                <div className="text-2xl font-bold text-indigo-900 dark:text-indigo-100 print:text-gray-900">
                  {formatCurrency(totalNOI || 0)}
                </div>
                <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1 print:text-gray-600">
                  Netto-Betriebsertrag/Jahr
                </p>
              </div>

              {/* Cap Rate */}
              <div className="p-4 bg-white dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800 print:bg-gray-50 print:border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400 uppercase tracking-wide print:text-indigo-900">
                    Cap Rate
                  </span>
                  <Percent className="w-4 h-4 text-indigo-500" />
                </div>
                <div className={`text-2xl font-bold ${
                  (portfolioCapRate || 0) >= 8 ? 'text-green-600 dark:text-green-400' :
                  (portfolioCapRate || 0) >= 6 ? 'text-blue-600 dark:text-blue-400' :
                  (portfolioCapRate || 0) >= 4 ? 'text-orange-600 dark:text-orange-400' :
                  'text-red-600 dark:text-red-400'
                } print:text-gray-900`}>
                  {formatPercent(portfolioCapRate || 0)}
                </div>
                <p className={`text-xs mt-1 font-medium ${
                  (portfolioCapRate || 0) >= 8 ? 'text-green-600 dark:text-green-400' :
                  (portfolioCapRate || 0) >= 6 ? 'text-blue-600 dark:text-blue-400' :
                  (portfolioCapRate || 0) >= 4 ? 'text-orange-600 dark:text-orange-400' :
                  'text-red-600 dark:text-red-400'
                } print:text-gray-600`}>
                  {(portfolioCapRate || 0) >= 8 ? '⭐ Exzellent' :
                   (portfolioCapRate || 0) >= 6 ? '✓ Gut' :
                   (portfolioCapRate || 0) >= 4 ? '○ Durchschnitt' :
                   '⚠ Unterdurchschnitt'}
                </p>
              </div>

              {/* Income Value (Ertragswert) */}
              <div className="p-4 bg-white dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800 print:bg-gray-50 print:border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400 uppercase tracking-wide print:text-indigo-900">
                    Ertragswert
                  </span>
                  <Building className="w-4 h-4 text-indigo-500" />
                </div>
                <div className="text-2xl font-bold text-indigo-900 dark:text-indigo-100 print:text-gray-900">
                  {formatCurrency(totalIncomeValue || 0)}
                </div>
                <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1 print:text-gray-600">
                  Einkommensbasierter Wert
                </p>
              </div>

              {/* Value Deviation */}
              <div className="p-4 bg-white dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800 print:bg-gray-50 print:border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400 uppercase tracking-wide print:text-indigo-900">
                    Abweichung
                  </span>
                  {(portfolioValueDeviation || 0) >= 0 ? 
                    <TrendingUp className="w-4 h-4 text-green-500" /> : 
                    <TrendingDown className="w-4 h-4 text-red-500" />
                  }
                </div>
                <div className={`text-2xl font-bold ${
                  (portfolioValueDeviation || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                } print:text-gray-900`}>
                  {(portfolioValueDeviation || 0) >= 0 ? '+' : ''}{formatPercent(portfolioValueDeviation || 0)}
                </div>
                <p className={`text-xs mt-1 font-medium ${
                  (portfolioValueDeviation || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                } print:text-gray-600`}>
                  {(portfolioValueDeviation || 0) >= 0 ? '⬆ positive Modellabweichung' : '⬇ negative Modellabweichung'}
                </p>
              </div>
            </div>

            {/* Explanation Box */}
            <div className="mt-4 p-3 bg-indigo-50 dark:bg-indigo-900/10 rounded-lg border border-indigo-100 dark:border-indigo-800 print:bg-gray-50 print:border-gray-200">
              <p className="text-xs text-indigo-700 dark:text-indigo-300 leading-relaxed print:text-gray-700">
                <strong>ℹ️ Berechnung:</strong> NOI = Mieteinnahmen − Betriebskosten (22%) | 
                Cap Rate = NOI / Marktwert × 100 | 
                Ertragswert = NOI / (Cap Rate / 100) | 
                Abweichung = (Ertragswert − Marktwert) / Marktwert × 100
              </p>
              <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-2 print:text-gray-600">
                <strong>Hinweis:</strong> Positive Abweichung deutet auf Unterbewertung hin (gute Investitionschance). 
                Negative Abweichung deutet auf Überbewertung hin (Vorsicht geboten).
              </p>
            </div>
          </CardContent>
        </Card>


        {/* Charts */}
        <div className="chart-section grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Yield Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-green-600" />
                Rendite nach Immobilie
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Bar 
                data={yieldChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: true,
                  aspectRatio: 2,
                  plugins: {
                    legend: { 
                      display: false,
                      labels: { color: '#1f2937' }
                    },
                    datalabels: {
                      display: false
                    }
                  },
                  scales: {
                    x: {
                      ticks: { 
                        color: '#1f2937', 
                        font: { size: 10 },
                        maxRotation: 45,
                        minRotation: 45
                      },
                      grid: { display: false },
                      border: { color: '#d1d5db', width: 1 }
                    },
                    y: {
                      beginAtZero: true,
                      ticks: {
                        color: '#1f2937',
                        font: { size: 11 },
                        callback: (value) => `${value}%`
                      },
                      grid: { color: '#f3f4f6', lineWidth: 0.5 },
                      border: { color: '#d1d5db', width: 1 }
                    }
                  }
                }}
              />
            </CardContent>
          </Card>

          {/* Cashflow Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <DollarSign className="w-5 h-5 mr-2 text-blue-600" />
                Cashflow nach Immobilie
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Bar 
                data={cashflowChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: true,
                  aspectRatio: 2,
                  plugins: {
                    legend: { 
                      display: false,
                      labels: { color: '#1f2937' }
                    },
                    datalabels: {
                      display: false
                    }
                  },
                  scales: {
                    x: {
                      ticks: { 
                        color: '#1f2937', 
                        font: { size: 10 },
                        maxRotation: 45,
                        minRotation: 45
                      },
                      grid: { display: false },
                      border: { color: '#d1d5db', width: 1 }
                    },
                    y: {
                      ticks: {
                        color: '#1f2937',
                        font: { size: 11 },
                        callback: (value) => formatCurrency(value)
                      },
                      grid: { color: '#f3f4f6', lineWidth: 0.5 },
                      border: { color: '#d1d5db', width: 1 }
                    }
                  }
                }}
              />
            </CardContent>
          </Card>
        </div>

        {/* Top and Bottom Performers */}
        {showPerformers && (
        <div className="performer-section grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Top Performers */}
          <Card className="border-green-200 dark:border-green-800">
            <CardHeader className="bg-green-50 dark:bg-green-900/20">
              <CardTitle className="flex items-center text-green-700 dark:text-green-300">
                <Award className="w-5 h-5 mr-2" />
                Top Performer
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {topPerformers.map((property, index) => (
                  <div key={property.id} className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/10 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">{property.name}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {formatPercent(property.occupancy)} Auslastung
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-600">{formatPercent(property.grossYield)}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Rendite</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Bottom Performers */}
          <Card className="border-orange-200 dark:border-orange-800">
            <CardHeader className="bg-orange-50 dark:bg-orange-900/20">
              <CardTitle className="flex items-center text-orange-700 dark:text-orange-300">
                <AlertTriangle className="w-5 h-5 mr-2" />
                Verbesserungspotenzial
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {bottomPerformers.map((property, index) => (
                  <div key={property.id} className="flex items-center justify-between p-4 bg-orange-50 dark:bg-orange-900/10 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">{property.name}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {formatPercent(property.occupancy)} Auslastung
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-orange-600">{formatPercent(property.grossYield)}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Rendite</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
        )}

        {/* Detailed Property Table */}
        <Card className="property-details-section mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Building className="w-5 h-5 mr-2 text-blue-600" />
              Alle Immobilien im Detail
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Immobilie</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">
                      <span className="inline-flex items-center gap-1">
                        Ertragswert*
                        <span className="group relative inline-block">
                          <svg className="w-4 h-4 text-indigo-500 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          <div className="invisible group-hover:visible absolute z-50 w-72 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg -left-32 top-6">
                            <p className="font-bold mb-2">* gem. Ertragswertverfahren</p>
                            <p className="mb-1"><strong>Berechnung:</strong></p>
                            <p className="mb-1">Ertragswert = NOI / (Cap Rate / 100)</p>
                            <p className="mb-1">NOI = Mieteinnahmen × 0,78 (22% Betriebskosten)</p>
                            <p>Cap Rate = Markt-Cap-Rate basierend auf Lage (PLZ)</p>
                          </div>
                        </span>
                      </span>
                    </th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Rendite</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Auslastung</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Cashflow</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">DSCR</th>
                  </tr>
                </thead>
                <tbody>
                  {properties?.map((property) => (
                    <tr key={property.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{property.name}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{property.address}</p>
                        </div>
                      </td>
                      <td className="text-right py-3 px-4 text-gray-900 dark:text-white font-semibold">
                        {formatCurrency(property.incomeValue || property.marketValue)}
                      </td>
                      <td className="text-right py-3 px-4">
                        <span className={`font-semibold ${
                          property.grossYield >= 8 ? 'text-green-600' :
                          property.grossYield >= 5 ? 'text-blue-600' :
                          'text-orange-600'
                        }`}>
                          {formatPercent(property.grossYield)}
                        </span>
                      </td>
                      <td className="text-right py-3 px-4">
                        <span className={`font-semibold ${
                          property.occupancy >= 95 ? 'text-green-600' :
                          property.occupancy >= 80 ? 'text-blue-600' :
                          'text-orange-600'
                        }`}>
                          {formatPercent(property.occupancy)}
                        </span>
                      </td>
                      <td className="text-right py-3 px-4">
                        <span className={`font-semibold ${
                          property.cashflow >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {formatCurrency(property.cashflow)}
                        </span>
                      </td>
                      <td className="text-right py-3 px-4">
                        <span className={`font-semibold ${
                          property.dscr >= 1.5 ? 'text-green-600' :
                          property.dscr >= 1.2 ? 'text-blue-600' :
                          property.dscr >= 1.0 ? 'text-orange-600' :
                          'text-red-600'
                        }`}>
                          {property.dscr != null && isFinite(property.dscr) ? property.dscr.toFixed(2) : property.dscr == null ? 'N/A' : '∞'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Mieterliste - Grouped by Property */}
        {data && data.properties && data.properties.some(p => p.tenants && p.tenants.length > 0) && (
          <div className="mt-8 mb-8 mieterliste-section">
            <Card>
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20">
                <CardTitle className="flex items-center text-indigo-700 dark:text-indigo-300">
                  <Users className="w-5 h-5 mr-2" />
                  📋 Mieterliste
                </CardTitle>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Übersicht aller aktiven Mietverhältnisse gruppiert nach Immobilie
                </p>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-8">
                  {data.properties
                    .filter(property => property.tenants && property.tenants.length > 0)
                    .map((property) => (
                      <div key={property.id} className="tenant-property-block border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                        {/* Property Header */}
                        <div className="bg-gray-50 dark:bg-slate-900 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Building className="w-5 h-5 text-indigo-600" />
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {property.name}
                              </h3>
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {property.tenants.length} {property.tenants.length === 1 ? 'Mieter' : 'Mieter'}
                            </div>
                          </div>
                        </div>

                        {/* Tenant Table */}
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="bg-gray-100 dark:bg-slate-800/50 border-b border-gray-200 dark:border-gray-700">
                                <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700 dark:text-gray-300">Einheit</th>
                                <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700 dark:text-gray-300">Mieter</th>
                                <th className="text-center py-3 px-4 font-semibold text-sm text-gray-700 dark:text-gray-300">Typ</th>
                                <th className="text-right py-3 px-4 font-semibold text-sm text-gray-700 dark:text-gray-300">Kaltmiete</th>
                                <th className="text-right py-3 px-4 font-semibold text-sm text-gray-700 dark:text-gray-300">Mietbeginn</th>
                                <th className="text-right py-3 px-4 font-semibold text-sm text-gray-700 dark:text-gray-300">NK netto</th>
                                <th className="text-right py-3 px-4 font-semibold text-sm text-gray-700 dark:text-gray-300">MwSt</th>
                                <th className="text-right py-3 px-4 font-semibold text-sm text-gray-700 dark:text-gray-300">Gesamtmiete</th>
                                <th className="text-right py-3 px-4 font-semibold text-sm text-gray-700 dark:text-gray-300">Kaution</th>
                              </tr>
                            </thead>
                            <tbody>
                              {property.tenants.map((tenant, index) => (
                                <tr 
                                  key={index} 
                                  className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                                >
                                  <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white">
                                    {tenant.unitId || '-'}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-700 dark:text-gray-300">
                                    {tenant.tenantName || 'Unbekannt'}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-center">
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                                      tenant.tenantType === 'commercial' 
                                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' 
                                        : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                    }`}>
                                      {tenant.tenantType === 'commercial' ? 'Gewerbe' : 'Wohnen'}
                                    </span>
                                  </td>
                                  <td className="py-3 px-4 text-sm text-right font-semibold text-gray-900 dark:text-white">
                                    {formatCurrencyDetailed(tenant.coldRent || 0)}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-right text-gray-700 dark:text-gray-300">
                                    {tenant.moveInDate ? new Date(tenant.moveInDate).toLocaleDateString('de-DE') : '-'}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-right text-gray-700 dark:text-gray-300">
                                    {formatCurrencyDetailed(tenant.utilitiesNet || 0)}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-right text-gray-700 dark:text-gray-300">
                                    {formatCurrencyDetailed(tenant.vat || 0)}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-right font-semibold text-indigo-600 dark:text-indigo-400">
                                    {formatCurrencyDetailed(tenant.totalRent || 0)}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-right text-gray-700 dark:text-gray-300">
                                    {formatCurrencyDetailed(tenant.deposit || 0)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            {/* Property Subtotal */}
                            <tfoot>
                              <tr className="bg-gray-50 dark:bg-slate-900 border-t-2 border-gray-300 dark:border-gray-600">
                                <td colSpan="3" className="py-3 px-4 text-sm font-bold text-gray-900 dark:text-white">
                                  Summe {property.name}
                                </td>
                                <td className="py-3 px-4 text-sm text-right font-bold text-gray-900 dark:text-white">
                                  {formatCurrencyDetailed(property.tenants.reduce((sum, t) => sum + (t.coldRent || 0), 0))}
                                </td>
                                <td className="py-3 px-4"></td>
                                <td className="py-3 px-4 text-sm text-right font-bold text-gray-900 dark:text-white">
                                  {formatCurrencyDetailed(property.tenants.reduce((sum, t) => sum + (t.utilitiesNet || 0), 0))}
                                </td>
                                <td className="py-3 px-4 text-sm text-right font-bold text-gray-900 dark:text-white">
                                  {formatCurrencyDetailed(property.tenants.reduce((sum, t) => sum + (t.vat || 0), 0))}
                                </td>
                                <td className="py-3 px-4 text-sm text-right font-bold text-indigo-600 dark:text-indigo-400">
                                  {formatCurrencyDetailed(property.tenants.reduce((sum, t) => sum + (t.totalRent || 0), 0))}
                                </td>
                                <td className="py-3 px-4 text-sm text-right font-bold text-gray-900 dark:text-white">
                                  {formatCurrencyDetailed(property.tenants.reduce((sum, t) => sum + (t.deposit || 0), 0))}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    ))}

                  {/* Portfolio Total (if multiple properties) */}
                  {data.properties.filter(p => p.tenants && p.tenants.length > 0).length > 1 && (
                    <div className="portfolio-summary-box bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-lg p-6 border-2 border-indigo-200 dark:border-indigo-700">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Portfolio-Kaltmiete</p>
                          <p className="text-xl font-bold text-gray-900 dark:text-white">
                            {formatCurrency(
                              data.properties
                                .filter(p => p.tenants && p.tenants.length > 0)
                                .reduce((sum, p) => sum + p.tenants.reduce((s, t) => s + (t.coldRent || 0), 0), 0)
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Portfolio-NK netto</p>
                          <p className="text-xl font-bold text-gray-900 dark:text-white">
                            {formatCurrency(
                              data.properties
                                .filter(p => p.tenants && p.tenants.length > 0)
                                .reduce((sum, p) => sum + p.tenants.reduce((s, t) => s + (t.utilitiesNet || 0), 0), 0)
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Portfolio-Gesamtmiete</p>
                          <p className="text-xl font-bold text-indigo-600 dark:text-indigo-400">
                            {formatCurrency(
                              data.properties
                                .filter(p => p.tenants && p.tenants.length > 0)
                                .reduce((sum, p) => sum + p.tenants.reduce((s, t) => s + (t.totalRent || 0), 0), 0)
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Portfolio-Kautionen</p>
                          <p className="text-xl font-bold text-gray-900 dark:text-white">
                            {formatCurrency(
                              data.properties
                                .filter(p => p.tenants && p.tenants.length > 0)
                                .reduce((sum, p) => sum + p.tenants.reduce((s, t) => s + (t.deposit || 0), 0), 0)
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
    </div>
    </>
  );
};


export default PerformanceReportPage;

